import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { PASSWORD_MIN, type SetupTokenInfo } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { PoweredBy, Wordmark } from '../lib/Brand';
import { setAppTitle } from '../lib/format';

/**
 * Landing page for invite and password-reset links: /admin/set-password#<token>. The token is in
 * the fragment so it never reaches server logs; it's sent to the API in a POST body.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(() => window.location.hash.slice(1));
  const [info, setInfo] = useState<SetupTokenInfo | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAppTitle('Set your password');
    // Drop the token from the address bar so it isn't left in history or shared by accident.
    window.history.replaceState(null, '', window.location.pathname);
    if (token.length < 20) {
      setInvalid('This link is incomplete. Open the full link you were sent.');
      return;
    }
    api.checkSetupToken(token).then(setInfo, (err) =>
      setInvalid(err instanceof ApiError ? err.message : "Couldn't check this link. Try again."),
    );
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.setPassword(token, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) setInvalid(err.message);
      else setError(err instanceof ApiError ? (err.fields?.password ?? err.message) : 'Something went wrong.');
      setBusy(false);
    }
  }

  return (
    <main className="page narrow login">
      <div className="login-head">
        <Wordmark className="wordmark" />
        {info && (
          <p className="muted">
            {info.purpose === 'INVITE' ? `Welcome, ${info.displayName}. Choose a password to finish setting up your account.` : 'Choose a new password.'}
          </p>
        )}
      </div>
      {invalid ? (
        <div className="card stack">
          <p className="error">{invalid}</p>
          <Link className="btn" to="/admin/login">Go to login</Link>
        </div>
      ) : !info ? (
        <p className="center muted">Checking your link…</p>
      ) : (
        <form onSubmit={submit} className="stack card">
          <label className="field">
            <span>Email</span>
            <input type="email" autoComplete="username" value={info.email} readOnly />
          </label>
          <label className="field">
            <span>New password</span>
            <input type="password" autoComplete="new-password" minLength={PASSWORD_MIN} required value={password} onChange={(e) => setPassword(e.target.value)} />
            <small className="muted">At least {PASSWORD_MIN} characters. A short phrase is easiest to remember.</small>
          </label>
          <label className="field">
            <span>Type it again</span>
            <input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="btn primary big" disabled={busy}>{busy ? 'Saving…' : 'Save password and log in'}</button>
        </form>
      )}
      <PoweredBy />
    </main>
  );
}
